import React, {PropsWithChildren, useCallback, useEffect, useState} from "react";
import {MetaData} from "../models/meta.model";
import {Company} from "../models/company.model";
import {Invoice} from "../models/invoice.model";
import {Customer} from "../models/customer.model";
import {Job} from "../models/job.model";
import {ItemSection, AddOnItemSection, InvoiceOption} from "../models/item.model";
import {FileSection} from "../models/file.model";
import {CardFeeRecovery} from "../models/card-fee-recovery.model";
import {isOfflineMode} from "../utils/util-functions";


const replaceOptionCFRPropertiesWithNonCFR = (option: InvoiceOption): InvoiceOption => ({
    ...option,
    subtotal: option.subtotal_without_card_fee_recovery || option.subtotal,
    categories: option.categories.map((category) => ({
        ...category,
        total: category.total_without_card_fee_recovery || category.total,
        items: category.items.map((item) => ({
            ...item,
            total: item.total_without_card_fee_recovery || item.total,
            unit_price: item.unit_price_without_card_fee_recovery || item.unit_price,
        }))
    }))
})

interface ServerDataContextState {
    isAcornEnabled: boolean;
    isFallbackToPricesWithoutCardFeeRecovery: boolean;
    cardFeeRecovery: CardFeeRecovery | null;
    company: Company;
    invoice: Invoice;
    customer: Customer;
    billCustomer: Customer
    job: Job;
    fileSection: FileSection;
    itemSection: ItemSection;
    setMetaData: (metaData: MetaData) => void;
    isActiveLink: boolean;
    setIsActiveLink: (isActiveLink: boolean) => void;
    addOnItemSection: AddOnItemSection;
    updateInvoice: (invoice: Invoice) => void;
}

export const ServerDataContext = React.createContext({} as ServerDataContextState);

interface ServerDataProviderProps extends PropsWithChildren {

}


export const ServerDataProvider: React.FunctionComponent<ServerDataProviderProps> = ({children}) => {
    const [isAcornEnabled, setIsAcornEnabled] = useState<boolean>(false);
    const [isFallbackToPricesWithoutCardFeeRecovery, setIsFallbackToPricesWithoutCardFeeRecovery] = useState<boolean>(false);
    const [cardFeeRecovery, setCardFeeRecovery] = useState<CardFeeRecovery | null>(null);
    const [company, setCompany] = useState({} as Company);
    const [invoice, setInvoice] = useState({} as Invoice);
    const [customer, setCustomer] = useState({} as Customer);
    const [billCustomer, setBillCustomer] = useState<Customer>({} as Customer);
    const [job, setJob] = useState({} as Job);
    const [fileSection, setFiles] = useState({} as FileSection);
    const [itemSection, setItemSection] = useState({} as ItemSection);
    const [inited, setInited] = useState<boolean>(false);
    const [isActiveLink, setIsActiveLink] = useState<boolean>(true);
    const [addOnItemSection, setAddOnItemSection] = useState({} as AddOnItemSection);

    const setServerData = (metaData: MetaData) => {
        if (metaData) {
            setIsAcornEnabled(metaData.is_acorn_enabled);
            setCardFeeRecovery(metaData.card_fee_recovery)
            setCompany(metaData.company);
            setInvoiceAndUpdateCustomFields(metaData.invoice);
            setCustomer(metaData.customer);
            setJob(metaData.job);
            setFiles(metaData.file);
            setItemSection(metaData.item);
            setAddOnItemSection(metaData.add_on_item);
            setBillCustomer(metaData.bill_customer)
        }
    }

    const setInvoiceAndUpdateCustomFields = (invoice: Invoice) => {
        if (invoice) {
            for (const customField of (invoice.custom_fields || [])) {
                customField.value = customField.value.replaceAll('\\n', '\n');
            }
        }
        setInvoice(invoice);
    }

    const setMetaData = (newMetaData: MetaData) => {
        setServerData(newMetaData);
    }


    useEffect(() => {
        if (!inited) {
            setInited(true);

            const metadata: MetaData = (window as any).META_DATA;
            const isMultipleOptions = (metadata.item?.options?.length || 0) > 1
            const isMultipleAddOnOptions = (metadata.add_on_item?.options?.length || 0) > 1
            const isOffline = isOfflineMode();
            const canNotCalculateCardFeeRecoverySummary = isOffline && Boolean(metadata.card_fee_recovery?.is_enabled) && (isMultipleOptions || isMultipleAddOnOptions);

            // Acorn Widget requires online mode to work properly
            if (isOffline) {
                metadata.is_acorn_enabled = false;
            }

            setIsFallbackToPricesWithoutCardFeeRecovery(canNotCalculateCardFeeRecoverySummary);
            setServerData(canNotCalculateCardFeeRecoverySummary ? {
                ...metadata,
                item: {
                    ...metadata.item,
                    options: metadata.item.options.map(replaceOptionCFRPropertiesWithNonCFR),
                },
                add_on_item: {
                    ...metadata.add_on_item,
                    options: metadata.add_on_item.options.map(replaceOptionCFRPropertiesWithNonCFR),
                },
            } : metadata);
        }
    }, [inited]);

    return <ServerDataContext.Provider
        value={{
            isAcornEnabled,
            isFallbackToPricesWithoutCardFeeRecovery,
            cardFeeRecovery,
            customer, company, invoice, job, fileSection, itemSection, setMetaData, isActiveLink, setIsActiveLink,
            addOnItemSection, updateInvoice: setInvoice,
            billCustomer
        }}>
        {children}
    </ServerDataContext.Provider>
}